import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { PspContext } from "@/config/contexts";
import { AccessEnum } from "@/config/types";
import {
  getAccessToken,
  getActorId,
  getAdminSessionStash,
  setAccessTokenStorage,
  setActorIdStorage,
  setPreAuthTokenStorage,
  setRefreshTokenStorage,
  setSelectedMandateIdStorage,
} from "@/config/utils";
import { getUserMe } from "@/api/openapi";
import { translate } from "@/config/translator";
import { toast } from "react-toastify";

export function PrivateRoute() {
  const {
    user,
    setUser,
    setLang,
    selectedMandateId,
    setSelectedMandateId,
    setSelectedMandate,
    setCookieConsentDialogOpen,
  } = useContext(PspContext);
  const { lang } = useContext(PspContext);
  const [access, setAccess] = useState<AccessEnum>(AccessEnum.LOADING);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationState = useMemo(
    () => ({
      from: location,
    }),
    [location],
  );

  useEffect(() => {
    // an impersonation session with no way back to the admin identity
    // (storage cleared, different tab, ...) can't be safely reconstructed
    if (getActorId() && !getAdminSessionStash()) {
      setAccessTokenStorage(null);
      setPreAuthTokenStorage(null);
      setRefreshTokenStorage(null);
      setActorIdStorage(null);
      setAccess(AccessEnum.UNAUTHORIZED);
      return;
    }

    if (!getAccessToken()) {
      setAccess(AccessEnum.UNAUTHORIZED);
      return;
    }

    if (!user) {
      setAccess(AccessEnum.LOADING);

      getUserMe().then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setUser(data);
          if (!selectedMandateId) {
            setSelectedMandateId(data.mandateRights[0]?.mandate.id);
            setSelectedMandate(data.mandateRights[0]?.mandate);

            setSelectedMandateIdStorage(data.mandateRights[0]?.mandate.id);
          }
          setLang(data.language.short);
          setAccess(AccessEnum.AUTHORIZED);
        }
      });
    } else {
      setAccess(AccessEnum.AUTHORIZED);
    }
  }, [
    lang,
    navigate,
    selectedMandateId,
    setCookieConsentDialogOpen,
    setLang,
    setSelectedMandate,
    setSelectedMandateId,
    setUser,
    user,
  ]);

  if (access === AccessEnum.LOADING) {
    return <>{translate("loading", lang)}</>;
  }

  if (access === AccessEnum.AUTHORIZED && user && getAccessToken()) {
    return <Outlet />;
  }

  return <Navigate to="/login" state={navigationState} replace />;
}
